import { usePostHog } from '@posthog/react';
import { buttonStyle } from 'components/buttons/buttonStyle';
import { Button } from 'components/ui/button';
import useAuth from 'hooks/useAuth';
import { FormattedMessage } from 'react-intl';
import { useSetMyVacationMutation } from 'store/api/employees';
import { FeatureFlag } from 'types/features';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';

interface VacationButtonProps {
    employeeId: number;
    onVacation?: boolean | null;
}

/**
 * Self-service quick action letting the logged-in user flag themselves on/off vacation
 * (VACATION_DECLARER). Renders nothing unless the flag is enabled and the profile shown is the
 * current user's own — the toggle only ever acts on the caller's own employee record.
 */
export default function VacationButton({ employeeId, onVacation }: VacationButtonProps) {
    const { user } = useAuth();
    const posthog = usePostHog();
    const isEnabled = useIsFeatureEnabled(FeatureFlag.VACATION_DECLARER);
    const [setMyVacation, { isLoading }] = useSetMyVacationMutation();

    if (!isEnabled || Number(user?.id) !== Number(employeeId)) {
        return null;
    }

    return (
        <Button
            {...buttonStyle({ variant: 'outlined', color: 'secondary' })}
            disabled={isLoading}
            onClick={() => {
                void setMyVacation(!onVacation)
                    .unwrap()
                    .then(() => {
                        posthog.capture('vacation_status_updated', { on_vacation: !onVacation });
                    })
                    .catch(console.error);
            }}
        >
            <FormattedMessage id={onVacation ? 'employees.im-back-from-vacation' : 'employees.im-on-vacation'} />
        </Button>
    );
}
